import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle, ChevronDown } from "lucide-react";
import CountdownTimer from "@/components/CountdownTimer";
import CheckoutButton from "@/components/CheckoutButton";

const ease = [0.16, 1, 0.3, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 1.1 } },
};
const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease } },
};

function RevealLine({ text, className, delayStart = 0 }) {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom pb-1 -mb-1">
          <motion.span
            className="inline-block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.85, ease, delay: delayStart + i * 0.09 }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function HeroSection() {
  const { scrollY } = useScroll();
  const yContent = useTransform(scrollY, [0, 600], [0, 100]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.15]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        paddingTop: "80px",
        paddingBottom: "80px",
        background: `
          radial-gradient(ellipse 110% 55% at 50% -5%, rgba(201,168,76,0.13) 0%, transparent 60%),
          radial-gradient(ellipse 70% 35% at 85% 90%, rgba(201,168,76,0.05) 0%, transparent 50%),
          #0A0A0A
        `,
      }}
    >
      {/* Breathing glow */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          top: "-15%",
          left: "50%",
          x: "-50%",
          width: 900,
          height: 520,
          background: "radial-gradient(ellipse, rgba(201,168,76,0.14) 0%, transparent 65%)",
        }}
        animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.08, 1] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        className="max-w-3xl mx-auto w-full relative"
        style={{ y: yContent, opacity: fade }}
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.15 }}
          className="mb-8"
        >
          <span className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase">
            9 Module · Lebenslanger Zugang
          </span>
        </motion.div>

        {/* Headline — word-by-word reveal */}
        <h1
          className="text-4xl sm:text-5xl md:text-6xl font-black leading-[1.06] tracking-tight mb-6"
          style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}
        >
          <RevealLine text="Baue Websites mit KI." className="text-[#F5F5F7]" delayStart={0.3} />
          <br />
          <RevealLine
            text="Verdiene 1.000–3.000€ pro Website."
            className="text-[#C9A84C]"
            delayStart={0.62}
          />
        </h1>

        <motion.div variants={container} initial="hidden" animate="show">
          {/* Sub */}
          <motion.p
            variants={item}
            className="text-lg sm:text-xl text-[#86868b] max-w-xl mx-auto mb-12 leading-relaxed"
          >
            Ohne Vorkenntnisse. Ohne Code. Erster Kunde in unter 24 Stunden möglich.
          </motion.p>

          {/* Countdown */}
          <motion.div
            variants={item}
            className="mb-10 inline-block"
            data-testid="hero-countdown-wrapper"
          >
            <p className="text-[#E84500] text-[11px] font-bold uppercase tracking-[0.25em] mb-4">
              Sonderangebot endet in
            </p>
            <CountdownTimer />
            <div className="mt-4 flex items-baseline justify-center gap-3">
              <span className="text-[#86868b] line-through text-base">499€</span>
              <span
                className="text-[#C9A84C] text-3xl font-black"
                style={{ textShadow: "0 0 20px rgba(201,168,76,0.35)" }}
                data-testid="launch-price"
              >
                299€
              </span>
              <span className="text-[#86868b] text-sm">einmalig</span>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={item}>
            <CheckoutButton testId="hero-cta-button" />
          </motion.div>

          {/* Trust */}
          <motion.div
            variants={item}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1"
          >
            {["Einmalzahlung", "Sofortiger Zugang", "Bereits 127+ Teilnehmer"].map((t, i) => (
              <span key={i} className="flex items-center gap-1.5 text-[#86868b] text-sm">
                <CheckCircle className="w-3.5 h-3.5 text-[#C9A84C]" />
                {t}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 0.8 }}
        data-testid="scroll-indicator"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-5 h-5 text-[#C9A84C]/70" />
        </motion.div>
      </motion.div>
    </section>
  );
}
